import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Bookmark, MapPin, Navigation, Trash2, Share2, Users, ChevronLeft } from 'lucide-react';

/**
 * SavedPointModal - create and manage reusable saved points (waypoints).
 * Boat 360 Plus feature. Points can optionally be shared with crew.
 */
const SavedPointModal = ({
  isOpen,
  onClose,
  onCreatePoint,
  onDeletePoint,
  onSharePoint,
  onNavigatePoint,
  savedPoints = [],
  friends = [],
  pendingLatLng,   // {lat, lon} when dropped via long-press
  myLocation,
}) => {
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  // Default to create view when a location was dropped, else the list
  const [view, setView] = useState(pendingLatLng ? 'create' : 'list');
  const [sharingPointId, setSharingPointId] = useState(null);

  // Keep view in sync when modal opens with/without a dropped location
  React.useEffect(() => {
    if (isOpen) {
      setView(pendingLatLng ? 'create' : 'list');
      setName('');
      setNotes('');
      setSharingPointId(null);
    }
  }, [isOpen, pendingLatLng]);

  // Location to save: dropped point takes priority, otherwise current location
  const targetLatLng = pendingLatLng || (myLocation ? { lat: myLocation.lat, lon: myLocation.lon } : null);

  const handleCreate = async () => {
    if (!name.trim() || !targetLatLng) return;
    setIsCreating(true);
    try {
      await onCreatePoint({
        name: name.trim(),
        lat: targetLatLng.lat,
        lon: targetLatLng.lon,
        notes: notes.trim() || null,
      });
      setName('');
      setNotes('');
      setView('list');
    } catch (error) {
      console.error('Failed to create point:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const myPoints = savedPoints.filter((p) => p.is_mine);
  const crewPoints = savedPoints.filter((p) => !p.is_mine);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bookmark className="w-5 h-5 text-cyan-600" />
            Saved Points
          </DialogTitle>
          <DialogDescription>
            Save your favorite spots — fishing holes, anchorages, hazards — and navigate back anytime.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 mb-2">
          <Button
            variant={view === 'create' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setView('create')}
            className="flex-1"
            data-testid="point-create-tab"
          >
            Add Point
          </Button>
          <Button
            variant={view === 'list' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setView('list')}
            className="flex-1"
            data-testid="point-list-tab"
          >
            My Points ({myPoints.length})
          </Button>
        </div>

        <ScrollArea className="flex-1 pr-4">
          {view === 'create' ? (
            <div className="space-y-4">
              <div>
                <Label htmlFor="point-name" className="text-sm font-medium">
                  Point Name
                </Label>
                <Input
                  id="point-name"
                  placeholder="e.g., Honey Hole, North Anchorage"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1"
                  maxLength={100}
                  data-testid="point-name-input"
                />
              </div>

              <div>
                <Label htmlFor="point-notes" className="text-sm font-medium">
                  Notes (optional)
                </Label>
                <Textarea
                  id="point-notes"
                  placeholder="e.g., 18ft, good structure on the east side"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="mt-1"
                  rows={3}
                  maxLength={500}
                  data-testid="point-notes-input"
                />
              </div>

              {targetLatLng ? (
                <div className="p-3 bg-cyan-50 rounded-lg">
                  <p className="text-xs text-cyan-700 flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {pendingLatLng ? 'Saving the spot you tapped on the map:' : 'Saving your current location:'}
                  </p>
                  <p className="text-xs text-cyan-600 font-mono mt-1">
                    {targetLatLng.lat.toFixed(5)}, {targetLatLng.lon.toFixed(5)}
                  </p>
                  {!pendingLatLng && (
                    <p className="text-[11px] text-cyan-700/70 mt-1">
                      Tip: long-press anywhere on the map to drop a point at that spot.
                    </p>
                  )}
                </div>
              ) : (
                <div className="p-3 bg-amber-50 rounded-lg">
                  <p className="text-xs text-amber-700">
                    No location yet. Long-press the map to drop a point, or enable location services.
                  </p>
                </div>
              )}
            </div>
          ) : sharingPointId ? (
            /* Friend picker for sharing a point */
            <div className="space-y-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSharingPointId(null)}
                className="text-xs"
                data-testid="share-back-btn"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <Label className="text-xs font-semibold text-gray-500 uppercase">
                Share with crew
              </Label>
              {friends.length === 0 ? (
                <p className="text-sm text-gray-500 py-4 text-center">
                  Add crew in the Crew tab to share points.
                </p>
              ) : (
                <div className="space-y-2">
                  {friends.map((f) => (
                    <Button
                      key={f.friend_user_id}
                      variant="outline"
                      className="w-full justify-start"
                      onClick={async () => {
                        await onSharePoint(sharingPointId, f.friend_user_id);
                        setSharingPointId(null);
                      }}
                      data-testid={`share-point-with-${f.friend_user_id}`}
                    >
                      <Users className="w-4 h-4 mr-2 text-cyan-600" />
                      {f.display_name}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            /* List of saved points */
            <div className="space-y-4">
              {myPoints.length > 0 && (
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase">
                    Your Points ({myPoints.length})
                  </Label>
                  <div className="space-y-2 mt-2">
                    {myPoints.map((point) => (
                      <Card key={point.id} className="border-cyan-200">
                        <CardContent className="p-3">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-start gap-2 min-w-0">
                              <div className="w-8 h-8 bg-cyan-600 rounded-lg flex items-center justify-center shrink-0">
                                <Bookmark className="w-4 h-4 text-white" />
                              </div>
                              <div className="min-w-0">
                                <p className="font-medium text-sm truncate">{point.name}</p>
                                {point.notes && (
                                  <p className="text-xs text-gray-500 line-clamp-2">{point.notes}</p>
                                )}
                                <p className="text-[11px] text-gray-400 font-mono mt-0.5">
                                  {point.lat.toFixed(4)}, {point.lon.toFixed(4)}
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-1 mt-2">
                            <Button
                              size="sm"
                              className="h-8 flex-1 bg-blue-500 hover:bg-blue-600 text-xs"
                              onClick={() => onNavigatePoint(point)}
                              data-testid={`navigate-point-${point.id}`}
                            >
                              <Navigation className="w-3.5 h-3.5 mr-1" /> Navigate
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 text-xs"
                              onClick={() => setSharingPointId(point.id)}
                              data-testid={`share-point-${point.id}`}
                            >
                              <Share2 className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 text-red-500 hover:text-red-600"
                              onClick={() => onDeletePoint(point.id)}
                              data-testid={`delete-point-${point.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {crewPoints.length > 0 && (
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase">
                    Shared by Crew ({crewPoints.length})
                  </Label>
                  <div className="space-y-2 mt-2">
                    {crewPoints.map((point) => (
                      <Card key={point.id} className="border-gray-200">
                        <CardContent className="p-3">
                          <div className="flex items-start gap-2">
                            <div className="w-8 h-8 bg-gray-400 rounded-lg flex items-center justify-center shrink-0">
                              <Bookmark className="w-4 h-4 text-white" />
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-sm truncate">{point.name}</p>
                              {point.notes && (
                                <p className="text-xs text-gray-500 line-clamp-2">{point.notes}</p>
                              )}
                              <p className="text-[11px] text-gray-400">by {point.owner_name}</p>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            className="h-8 w-full mt-2 bg-blue-500 hover:bg-blue-600 text-xs"
                            onClick={() => onNavigatePoint(point)}
                            data-testid={`navigate-crew-point-${point.id}`}
                          >
                            <Navigation className="w-3.5 h-3.5 mr-1" /> Navigate
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {savedPoints.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Bookmark className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>No saved points yet</p>
                  <p className="text-xs">Long-press the map or tap "Add Point" to save a spot.</p>
                </div>
              )}
            </div>
          )}
        </ScrollArea>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Close</Button>
          {view === 'create' && (
            <Button
              onClick={handleCreate}
              disabled={!name.trim() || !targetLatLng || isCreating}
              className="bg-cyan-600 hover:bg-cyan-700"
              data-testid="save-point-btn"
            >
              {isCreating ? 'Saving...' : 'Save Point'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SavedPointModal;
